import { useLayoutEffect, useRef, useState } from "react";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ArrowPathIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import { InboxChannel, InboxFolder } from "../../models/messagesInbox/IInboxConversation";
import classNames from "../../utils/classNames";
import { CHANNEL_LOGOS, useInboxLive } from "./InboxBits";

// Labels stay English here (they're the translation keys); they're translated where rendered.
export const MAIN_FOLDERS: { id: InboxFolder; label: string }[] = [
  { id: "needs_reply", label: "Needs reply" },
  { id: "pre_sales", label: "Pre-sales" },
  { id: "post_sales", label: "Post-sales" },
  { id: "returns", label: "Returns & cases" },
  // Rarely needed, so last (and first into More on a narrow screen).
  { id: "sent", label: "Sent" },
];

export const MORE_FOLDERS: { id: InboxFolder; label: string }[] = [
  { id: "no_reply_needed", label: "No reply needed" },
  { id: "all", label: "All" },
];

const CHANNELS: { id: InboxChannel | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "ebay", label: "eBay" },
  { id: "partpro", label: "PartPro" },
];

interface InboxTopBarProps {
  folder: InboxFolder;
  onFolder: (folder: InboxFolder) => void;
  counts: Record<InboxFolder, number>;
  channel: InboxChannel | "all";
  onChannel: (channel: InboxChannel | "all") => void;
  partProWaiting: boolean;
  search: string;
  onSearch: (search: string) => void;
  /** Live data: reload the list (and the open conversation) from eBay and PartPro. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** Live data: a cancel or refund is in flight, so the folders, channels and Refresh wait for it. */
  disabled?: boolean;
}

export default function InboxTopBar({ folder, onFolder, counts, channel, onChannel, partProWaiting, search, onSearch, onRefresh, refreshing = false, disabled = false }: InboxTopBarProps) {
  const { t } = useTranslation("messages");
  // "No reply needed" isn't stored anywhere yet, so live data has no such folder.
  const live = useInboxLive();
  const moreFolders = live ? MORE_FOLDERS.filter((f) => f.id !== "no_reply_needed") : MORE_FOLDERS;
  // No scrolling: tabs that don't fit the bar move into More (Needs reply always stays).
  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fitCount, setFitCount] = useState(MAIN_FOLDERS.length);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const measure = measureRef.current;
    if (!nav || !measure) return;
    const fit = () => {
      const widths = Array.from(measure.children).map((el) => (el as HTMLElement).offsetWidth + 4);
      const moreWidth = widths.pop() ?? 0;
      let used = moreWidth;
      let count = 0;
      while (count < widths.length && used + widths[count] <= nav.clientWidth) used += widths[count++];
      setFitCount(Math.max(1, count));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  const shown = MAIN_FOLDERS.slice(0, fitCount);
  const overflow = MAIN_FOLDERS.slice(fitCount);
  const moreSelected = [...overflow, ...moreFolders].find((f) => f.id === folder);

  return (
    <div className="flex h-12 shrink-0 items-center gap-4 border-b border-gray-200 bg-white px-4">
      <nav ref={navRef} className="relative flex min-w-0 flex-1 items-stretch gap-1 self-stretch overflow-hidden">
        {/* Invisible copy of every tab, only to measure how many fit. */}
        <div ref={measureRef} aria-hidden className="pointer-events-none invisible absolute flex">
          {MAIN_FOLDERS.map((f) => (
            <span key={f.id} className="flex shrink-0 items-center gap-1.5 px-2.5 text-[13px] font-medium">
              {t(f.label)}
              <span className="px-1.5 text-[12px]">{counts[f.id]}</span>
            </span>
          ))}
          <span className="flex shrink-0 items-center gap-1 px-2.5 text-[13px] font-medium">
            {t("More")} <ChevronDownIcon className="h-3.5 w-3.5" />
          </span>
        </div>

        {shown.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => onFolder(f.id)}
            disabled={disabled}
            className={classNames(
              "flex shrink-0 items-center gap-1.5 border-b-2 px-2.5 text-[13px] font-medium disabled:opacity-60",
              folder === f.id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-600 hover:text-gray-900"
            )}
          >
            {t(f.label)}
            {f.id === "needs_reply" ? (
              counts[f.id] > 0 && <span className="rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">{counts[f.id]}</span>
            ) : (
              <span className="text-[12px] text-gray-400">{counts[f.id]}</span>
            )}
          </button>
        ))}

        <Menu as="div" className="flex shrink-0">
          <MenuButton
            disabled={disabled}
            className={classNames(
              "flex items-center gap-1 border-b-2 px-2.5 text-[13px] font-medium disabled:opacity-60",
              moreSelected ? "border-primary-600 text-primary-700" : "border-transparent text-gray-600 hover:text-gray-900"
            )}
          >
            {moreSelected ? t(moreSelected.label) : t("More")}
            <ChevronDownIcon className="h-3.5 w-3.5" />
          </MenuButton>
          {/* Anchored, so it opens over the page instead of being cut off by the bar. */}
          <MenuItems anchor={{ to: "bottom start", gap: 4 }} className="z-40 w-48 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
            {[...overflow, ...moreFolders].map((f, i) => (
              <MenuItem key={f.id}>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={() => onFolder(f.id)}
                    className={classNames(
                      "flex w-full justify-between px-3 py-1.5 text-[13px]",
                      folder === f.id ? "font-semibold text-primary-700" : "text-gray-700",
                      focus && "bg-gray-50",
                      i === overflow.length && overflow.length > 0 && "mt-1 border-t border-gray-100 pt-2"
                    )}
                  >
                    {t(f.label)}
                    <span className={f.id === "needs_reply" && counts[f.id] > 0 ? "font-semibold text-red-600" : "text-gray-400"}>{counts[f.id]}</span>
                  </button>
                )}
              </MenuItem>
            ))}
          </MenuItems>
        </Menu>
      </nav>

      <div className="flex shrink-0 rounded-lg bg-gray-100 p-0.5">
        {CHANNELS.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onChannel(c.id)}
            disabled={disabled}
            className={classNames(
              "relative flex items-center gap-1.5 rounded-md px-3 py-1 text-[13px] font-medium disabled:opacity-60",
              channel === c.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
            )}
          >
            {c.id !== "all" && <img src={CHANNEL_LOGOS[c.id].src} alt="" className="h-4 w-4 object-contain" />}
            {c.id === "all" ? t(c.label) : c.label}
            {c.id === "partpro" && partProWaiting && <span title={t("PartPro buyers are waiting")} className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-red-600" />}
          </button>
        ))}
      </div>

      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing || disabled}
          title={t("Check eBay and PartPro for new messages")}
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <ArrowPathIcon className={classNames("h-4 w-4", refreshing && "animate-spin")} />
          {refreshing ? t("Refreshing…") : t("Refresh")}
        </button>
      )}

      <label className="relative hidden w-64 shrink-0 xl:block">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t("Search buyer, reg, order no., tag")}
          className="w-full rounded-lg border border-gray-200 py-1.5 pl-8 pr-2 text-[13px] focus:border-primary-500 focus:ring-primary-500"
        />
      </label>
    </div>
  );
}
